import { useFormik } from 'formik';
import lodash from 'lodash';
import * as yup from 'yup';
import { isValid, isBefore, isEqual, startOfToday, isoExtendedFormat, shortMonthDayYear, shortDayMonthDate, monthShortFormatter, dayOfMonthFormatter, hourMinuteFormat, meridianFormat } from '@amtrav/dates';
import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Flex, Box } from '@rebass/grid';
import { CardWithIcon, AirlineLogo, Text, Currency, Badge, EmptyState, InfoTooltip, IconFlex } from '@amtrav/view-components';
import { IconLine, IconOpenNew } from '@amtrav/icons';
import { parseDate } from '@amtrav/utils';
import { media, getThemeProp, MatchMedia } from '@amtrav/themes';
import { encodeObjToJsrString } from '@amtrav/jsr';
import { getTokenValue, THEME_TOKENS, SPACE } from '@amtrav/theme-system';

const FLIGHT_SEARCH_TYPES = {
  ROUND_TRIP: 'roundTrip',
  ONE_WAY: 'oneWay',
  MULTI_LEG: 'multiLeg'
};
const FLIGHT_TIME_OPTIONS = [{
  value: '0-2359',
  label: 'Anytime'
}, {
  value: '500-1200',
  label: 'Morning'
}, {
  value: '1130-1800',
  label: 'Afternoon'
}, {
  value: '1600-2359',
  label: 'Evening'
}, {
  value: '0-200',
  label: '12:00 am'
}, {
  value: '0-230',
  label: '12:30 am'
}, {
  value: '0-300',
  label: '1:00 am'
}, {
  value: '30-330',
  label: '1:30 am'
}, {
  value: '100-400',
  label: '2:00 am'
}, {
  value: '130-430',
  label: '2:30 am'
}, {
  value: '200-500',
  label: '3:00 am'
}, {
  value: '230-530',
  label: '3:30 am'
}, {
  value: '300-600',
  label: '4:00 am'
}, {
  value: '330-630',
  label: '4:30 am'
}, {
  value: '400-700',
  label: '5:00 am'
}, {
  value: '430-730',
  label: '5:30 am'
}, {
  value: '500-800',
  label: '6:00 am'
}, {
  value: '530-830',
  label: '6:30 am'
}, {
  value: '600-900',
  label: '7:00 am'
}, {
  value: '630-930',
  label: '7:30 am'
}, {
  value: '700-1000',
  label: '8:00 am'
}, {
  value: '730-1030',
  label: '8:30 am'
}, {
  value: '800-1100',
  label: '9:00 am'
}, {
  value: '830-1130',
  label: '9:30 am'
}, {
  value: '900-1200',
  label: '10:00 am'
}, {
  value: '930-1230',
  label: '10:30 am'
}, {
  value: '1000-1300',
  label: '11:00 am'
}, {
  value: '1030-1330',
  label: '11:30 am'
}, {
  value: '1100-1400',
  label: '12:00 pm'
}, {
  value: '1130-1430',
  label: '12:30 pm'
}, {
  value: '1200-1500',
  label: '1:00 pm'
}, {
  value: '1230-1530',
  label: '1:30 pm'
}, {
  value: '1300-1600',
  label: '2:00 pm'
}, {
  value: '1330-1630',
  label: '2:30 pm'
}, {
  value: '1400-1700',
  label: '3:00 pm'
}, {
  value: '1430-1730',
  label: '3:30 pm'
}, {
  value: '1500-1800',
  label: '4:00 pm'
}, {
  value: '1530-1830',
  label: '4:30 pm'
}, {
  value: '1600-1900',
  label: '5:00 pm'
}, {
  value: '1630-1930',
  label: '5:30 pm'
}, {
  value: '1700-2000',
  label: '6:00 pm'
}, {
  value: '1730-2030',
  label: '6:30 pm'
}, {
  value: '1800-2100',
  label: '7:00 pm'
}, {
  value: '1830-2130',
  label: '7:30 pm'
}, {
  value: '1900-2200',
  label: '8:00 pm'
}, {
  value: '1930-2230',
  label: '8:30 pm'
}, {
  value: '2000-2300',
  label: '9:00 pm'
}, {
  value: '2030-2330',
  label: '9:30 pm'
}, {
  value: '2100-2359',
  label: '10:00 pm'
}, {
  value: '2130-2359',
  label: '10:30 pm'
}, {
  value: '2200-2359',
  label: '11:00 pm'
}, {
  value: '2230-2359',
  label: '11:30 pm'
}];
// also available in getInvariantData
const AIRLINE_OPTIONS = [{
  value: 'YY',
  label: '(All)'
}, {
  value: 'O',
  label: 'Oneworld'
}, {
  value: 'S',
  label: 'SkyTeam'
}, {
  value: 'A',
  label: 'Star Alliance'
}, {
  value: 'ULCC',
  label: 'No Ultra Low Cost Carriers'
}, {
  value: 'EI',
  label: 'Aer Lingus'
}, {
  value: 'AM',
  label: 'Aeromexico'
}, {
  value: 'AC',
  label: 'Air Canada'
}, {
  value: 'AF',
  label: 'Air France'
}, {
  value: 'AI',
  label: 'Air India'
}, {
  value: 'AS',
  label: 'Alaska'
}, {
  value: 'AZ',
  label: 'Alitalia'
}, {
  value: 'NH',
  label: 'All Nippon Airways'
}, {
  value: 'AA',
  label: 'American'
}, {
  value: '2V',
  label: 'Amtrak'
}, {
  value: 'OZ',
  label: 'Asiana Airlines'
}, {
  value: 'AV',
  label: 'Avianca'
}, {
  value: 'MX',
  label: 'Breeze'
}, {
  value: 'BA',
  label: 'British Air'
}, {
  value: 'BW',
  label: 'Caribbean Airlines'
}, {
  value: 'CM',
  label: 'Copa Airlines'
}, {
  value: 'DL',
  label: 'Delta'
}, {
  value: 'EK',
  label: 'Emirates Airlines'
}, {
  value: 'F9',
  label: 'Frontier'
}, {
  value: 'HA',
  label: 'Hawaiian Airlines'
}, {
  value: 'IB',
  label: 'Iberia'
}, {
  value: 'JL',
  label: 'Japan Airlines'
}, {
  value: 'B6',
  label: 'jetBlue'
}, {
  value: 'KL',
  label: 'KLM'
}, {
  value: 'KE',
  label: 'Korean Air'
}, {
  value: 'LA',
  label: 'LATAM Airlines'
}, {
  value: 'LO',
  label: 'LOT Airlines'
}, {
  value: 'LH',
  label: 'Lufthansa'
}, {
  value: 'PD',
  label: 'Porter Airlines'
}, {
  value: 'QF',
  label: 'Qantas'
}, {
  value: 'SK',
  label: 'SAS'
}, {
  value: 'SQ',
  label: 'Singapore Airlines'
}, {
  value: 'WN',
  label: 'Southwest'
}, {
  value: 'NK',
  label: 'Spirit'
}, {
  value: 'SY',
  label: 'Sun Country'
}, {
  value: 'LX',
  label: 'Swiss'
}, {
  value: 'TA',
  label: 'Taca International'
}, {
  value: 'JJ',
  label: 'Tam Meridionais'
}, {
  value: 'TK',
  label: 'Turkish Airlines'
}, {
  value: 'UA',
  label: 'United'
}, {
  value: 'VX',
  label: 'Virgin America'
}, {
  value: 'VS',
  label: 'Virgin Atlantic'
}, {
  value: 'WS',
  label: 'Westjet'
}];
const ZERO_TO_NINE = [{
  value: '0',
  label: '0'
}, {
  value: '1',
  label: '1'
}, {
  value: '2',
  label: '2'
}, {
  value: '3',
  label: '3'
}, {
  value: '4',
  label: '4'
}, {
  value: '5',
  label: '5'
}, {
  value: '6',
  label: '6'
}, {
  value: '7',
  label: '7'
}, {
  value: '8',
  label: '8'
}, {
  value: '9',
  label: '9'
}];
const STOPS_OK = [{
  value: '0',
  label: 'Non-stop Only'
}, {
  value: '-1',
  label: 'Stops Ok'
}];
const REFUNDABLE_OPTIONS = [{
  value: 'true',
  label: 'Refundable Fares Only'
}, {
  value: 'false',
  label: 'Lowest Fare (Non-Refundable)'
}];
const CABIN_OPTIONS = [{
  value: 'Y',
  label: 'Lowest Fare'
}, {
  value: 'W',
  label: 'Premium Economy'
}, {
  value: 'C',
  label: 'Business or First'
}, {
  value: 'F',
  label: 'First Class'
}];

const passengerCount = values => values.adults + values.seniors + values.children + values.infants;

// If business travel, and not airAllowMultiPax, and no invitationToken, only 1 passenger allowed.
function canBookAdditionalBusinessPassengers(values) {
  const {
    isPersonal,
    airAllowMultiPax,
    invitationToken
  } = this.options.context;
  const noToken = invitationToken === undefined || invitationToken === null;
  if (!isPersonal && !airAllowMultiPax && noToken) {
    return passengerCount(values) === 1;
  }
  return true;
}

// If a invitationToken is present, check the number of passengers allowed.
function checkAdditionalTravelerLimit(values) {
  const {
    invitationToken,
    additionalTravelers
  } = this.options.context;
  // This does not apply if an invitation token is not present
  if (!invitationToken) {
    return true;
  }
  const limit = additionalTravelers + 1;
  if (passengerCount(values) > limit) {
    // https://github.com/jquense/yup/issues/1128
    // For custom error messages we can throw an error instead of returning false
    return this.createError({
      // This param is not really used because we are overriding the message anyway.
      params: {
        limit
      },
      message: `Traveler limit of ${limit} exceeded.`
    });
  }
  return true;
}
const passengerShape = yup.object().shape({
  adults: yup.number().integer().moreThan(-1),
  children: yup.number().integer().moreThan(-1),
  seniors: yup.number().integer().moreThan(-1),
  infants: yup.number().integer().moreThan(-1)
}).required().test('some-passengers', 'You must have at least one passenger.', values => {
  return passengerCount(values) > 0;
}).test('enough-laps', 'There must be at least one adult or senior for each infant.', values => {
  return values.adults + values.seniors >= values.infants;
}).test('additional-passengers-allowed', 'Only one passenger can be booked for business travel', canBookAdditionalBusinessPassengers).test({
  name: 'invite-additional-travelers',
  message: 'Additional traveler limit exceeded',
  test: checkAdditionalTravelerLimit
});
function dateRangeValidator$2() {
  const {
    departDate,
    returnDate
  } = this.parent;
  if (!isValid(returnDate)) {
    return true;
  }
  return isBefore(departDate, returnDate) || isEqual(departDate, returnDate);
}
function dateNotBeforeToday$2() {
  const {
    departDate
  } = this.parent;
  return !isBefore(departDate, startOfToday());
}
function dateNotBeforePreviousLeg() {
  if (this.options.context.searchType !== FLIGHT_SEARCH_TYPES.MULTI_LEG) {
    return true;
  }
  const allDates = this.options.context.legs.map(leg => leg.departDate);
  if (allDates < 2) {
    return true;
  }
  return allDates.every((date, idx, arr) => {
    if (idx + 1 < arr.length) {
      // Seems counter intuitive but if the next date hasn't been selected yet this test should pass.
      // The `required` rule will pick the more appropriate error here.
      const nextDate = arr[idx + 1];
      if (nextDate === null) {
        return true;
      }
      return date <= nextDate;
    }
    return true;
  });
}
const departDateSchema = yup.date().label('Depart date').typeError('Depart Date is required').test('is-not-before-today', 'Date cannot be in the past.', dateNotBeforeToday$2).test('is-not-before-previous-leg', "Date cannot be before previous leg's departure date.", dateNotBeforePreviousLeg);
const oneWayLeg = yup.object().shape({
  searchType: yup.string(),
  fromLoc: yup.string().typeError('From Location Required'),
  toLoc: yup.string().typeError('To Location Required'),
  departDate: departDateSchema,
  fromExclusionList: yup.array().of(yup.string()).ensure().test('some-options', 'You must have at least one airport option', function atLeastOne(valueArray) {
    // Due to user preferences we can have locations that don't exist in the location options
    // We will need to filter them out to prevent a false negative.
    const filteredValueArray = this.parent.allFromLocs.map(x => x.code).filter(value => valueArray.includes(value));
    if ((this.parent.allFromLocs.length || 0) === 0) return true;
    return filteredValueArray.length !== (this.parent.allFromLocs.length || 0);
  }),
  toExclusionList: yup.array().of(yup.string()).ensure().test('some-options', 'You must have at least one airport option', function atLeastOne(valueArray) {
    // Due to user preferences we can have locations that don't exist in the location options
    // We will need to filter them out to prevent a false negative.
    const filteredValueArray = this.parent.allToLocs.map(x => x.code).filter(value => valueArray.includes(value));
    if ((this.parent.allToLocs.length || 0) === 0) return true;
    return filteredValueArray.length !== (this.parent.allToLocs.length || 0);
  })
});
const roundTripLeg = oneWayLeg.shape({
  departDate: departDateSchema.test('valid-date-range', 'Date range is invalid.', dateRangeValidator$2),
  returnDate: yup.date().label('Return date').typeError('Return Date Required').test('valid-date-range', 'Date range is invalid.', dateRangeValidator$2)
});
var flightSchema = yup.object().shape({
  searchType: yup.string(),
  passengers: passengerShape,
  legs: yup.array().when('searchType', (type, schema) => {
    // modify the schema depending on the searchType
    return schema.of(type === FLIGHT_SEARCH_TYPES.ROUND_TRIP ? roundTripLeg : oneWayLeg);
  }).min(1)
});

const defaultLeg = {
  info: {},
  key: 'firstKey',
  fromLoc: null,
  toLoc: null,
  departDate: null,
  departTime: '0-2359',
  returnDate: null,
  returnTime: '0-2359',
  fromExclusionList: [],
  toExclusionList: [],
  allFromLocs: [],
  allToLocs: []
};
const defaultState$2 = {
  searchType: FLIGHT_SEARCH_TYPES.ROUND_TRIP,
  passengers: {
    adults: 1,
    children: 0,
    seniors: 0,
    infants: 0
  },
  cabin: 'Y',
  airline: 'YY',
  stops: -1,
  refundable: false,
  legs: [defaultLeg]
};
const mergeLegs = function () {
  let incomingLegs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const [firstInitialLeg = {}, ...additionalLegs] = incomingLegs;
  return [{
    ...defaultLeg,
    ...firstInitialLeg
  }, ...additionalLegs];
};
const getValuesForValidation = values => {
  if (values.searchType !== FLIGHT_SEARCH_TYPES.MULTI_LEG) {
    const trimmedValues = {
      ...values,
      legs: [values.legs[0]]
    };
    return trimmedValues;
  }
  return values;
};
const useFlightSearch = config => {
  var _config$initialValues;
  const mergedInitialState = {
    ...defaultState$2,
    ...config.initialValues
  };
  mergedInitialState.legs = mergeLegs(config === null || config === void 0 || (_config$initialValues = config.initialValues) === null || _config$initialValues === void 0 ? void 0 : _config$initialValues.legs);
  const formik = useFormik({
    initialValues: mergedInitialState,
    validate: values => {
      const valuesForValidation = getValuesForValidation(values);
      try {
        flightSchema.validateSync(valuesForValidation, {
          abortEarly: false,
          context: {
            ...values,
            ...(config.additionalContext || {})
          }
        });
      } catch (error) {
        if (error.name !== 'ValidationError') {
          throw error;
        }
        return error.inner.reduce((errors, currentError) => {
          const newErrors = lodash.set(errors, currentError.path, currentError.message);
          return newErrors;
        }, {});
      }
      return {};
    },
    validateOnBlur: true,
    onSubmit: async values => {
      config.onSubmit(values);
    }
  });
  const {
    handleBlur,
    submitForm,
    handleChange,
    setFieldTouched,
    setFieldValue
  } = formik;
  const getLegFieldTouched = (idx, field) => {
    var _formik$touched;
    return (_formik$touched = formik.touched) === null || _formik$touched === void 0 || (_formik$touched = _formik$touched.legs) === null || _formik$touched === void 0 || (_formik$touched = _formik$touched[idx]) === null || _formik$touched === void 0 ? void 0 : _formik$touched[field];
  };
  const getLegFieldError = (idx, field) => {
    var _formik$errors;
    return (_formik$errors = formik.errors) === null || _formik$errors === void 0 || (_formik$errors = _formik$errors.legs) === null || _formik$errors === void 0 || (_formik$errors = _formik$errors[idx]) === null || _formik$errors === void 0 ? void 0 : _formik$errors[field];
  };
  const setLegFieldValue = (idx, field, value) => {
    const {
      legs
    } = formik.values;
    legs[idx] = {
      ...legs[idx],
      [field]: value
    };
    setFieldValue('legs', [...legs]);
  };
  const setLegFieldTouched = (idx, field) => setFieldTouched(`legs[${idx}].${field}`);
  const setFieldTouchedAllLegs = field => {
    formik.values.legs.forEach((_, idx) => {
      setLegFieldTouched(idx, field);
    });
  };
  const actions = {
    setPassengersValue: values => {
      setFieldValue('passengers', {
        ...values
      });
      setFieldTouched('passengers');
    },
    setLegFieldValue,
    setLegFieldTouched,
    setFieldTouchedAllLegs,
    getLegFieldTouched,
    getLegFieldError,
    showLegErrorMessage: (idx, field) => {
      return getLegFieldTouched(idx, field) && getLegFieldError(idx, field);
    },
    setAirportOptsExclusionList: (current, code, value) => {
      if (value) {
        return current.filter(c => c !== code);
      }
      return [...new Set([...current, code])];
    },
    setFromAndToAirportOptions: (idx, data) => {
      setLegFieldValue(idx, 'allFromLocs', data.fromOptions || []);
      setLegFieldValue(idx, 'allToLocs', data.toOptions || []);
      setLegFieldTouched(idx, 'allFromLocs');
      setLegFieldTouched(idx, 'allToLocs');
    },
    submitForm,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    addLeg: () => {
      const {
        legs
      } = formik.values;
      const newLeg = {
        ...defaultLeg,
        key: String(Date.now()),
        fromLoc: legs[legs.length - 1].toLoc
      };
      setFieldValue('legs', [...legs, newLeg]);
    },
    removeLeg: key => {
      setFieldValue('legs', formik.values.legs.filter((leg, i) => {
        if (leg.key === key) {
          setFieldTouched(`legs[${i}]`, false);
          return false;
        }
        return true;
      }));
    },
    validateForm: formik.validateForm
  };
  const {
    values,
    errors,
    touched
  } = formik;
  return [{
    values,
    errors,
    touched
  }, actions];
};

const DEFAULT_AIRLINE_FILTER = 'YY';
const LOW_COST_CARRIER = 'ULCC';
const makeNumPax = passengers => {
  const {
    adults,
    seniors,
    children,
    infants
  } = passengers;
  return `${adults}${seniors}${children}0${infants}`;
};
const getAirlineView = airline => {
  return airline && airline.length === 2 ? airline : DEFAULT_AIRLINE_FILTER;
};
const getAlliance = alliance => {
  return alliance && alliance.length === 1 ? [alliance] : [];
};
const getExcludeULCC = code => {
  return !!(code && code === LOW_COST_CARRIER);
};
const getFromTime = timeString => timeString.split('-')[0];
const getToTime = timeString => timeString.split('-')[1];
const deriveLegValues = formValues => {
  const newFormValues = {
    ...formValues
  };
  const firstLeg = newFormValues.legs[0];
  if (newFormValues.searchType === FLIGHT_SEARCH_TYPES.ROUND_TRIP) {
    // add a second leg with the reversed to and from
    const returnLeg = {
      ...firstLeg,
      fromLoc: firstLeg.toLoc,
      toLoc: firstLeg.fromLoc,
      departDate: firstLeg.returnDate,
      departTime: firstLeg.returnTime
    };
    newFormValues.legs = [firstLeg, returnLeg];
  }
  if (newFormValues.searchType === FLIGHT_SEARCH_TYPES.ONE_WAY) {
    newFormValues.legs = [firstLeg];
  }
  return newFormValues;
};
const standardPayloadAdapter$2 = formValues => {
  const values = deriveLegValues(formValues);
  const payload = {
    legs: [],
    numpax: makeNumPax(values.passengers),
    cabinRequested: values.cabin,
    filters: {
      airportsToExclude: [],
      timeFilters: [],
      airlineView: getAirlineView(values.airline),
      alliances: getAlliance(values.airline),
      maxStops: values.stops,
      excludeULCC: getExcludeULCC(values.airline)
    },
    refundableOnly: values.refundable === 'true'
  };
  values.legs.forEach(leg => {
    payload.legs.push({
      fromLocId: leg.fromLoc,
      toLocId: leg.toLoc,
      date: isoExtendedFormat(leg.departDate)
    });
    const exclusionList = new Set([...leg.fromExclusionList, ...leg.toExclusionList]);
    payload.filters.airportsToExclude.push([...exclusionList]);
    payload.filters.timeFilters.push({
      departFromTime: getFromTime(leg.departTime),
      departToTime: getToTime(leg.departTime)
    });
  });
  return payload;
};

function dateRangeValidator$1() {
  const {
    pickUpDate,
    dropOffDate
  } = this.parent;
  if (!isValid(dropOffDate)) {
    return true;
  }
  return isBefore(pickUpDate, dropOffDate) || isEqual(pickUpDate, dropOffDate);
}
function dateNotBeforeToday$1() {
  const {
    pickUpDate
  } = this.parent;
  return !isBefore(pickUpDate, startOfToday());
}
var carSchema = yup.object().shape({
  pickUp: yup.string().typeError('Pickup Location is Required'),
  pickUpDate: yup.date().typeError('You must provide a pick up date.').test('is-not-before-today', 'Date cannot be in the past.', dateNotBeforeToday$1),
  dropOffDate: yup.date().typeError('You must provide a drop off date.').test('valid-date-range', 'Date range is invalid.', dateRangeValidator$1)
});

const defaultState$1 = {
  pickUp: null,
  dropOff: null,
  pickUpDate: null,
  pickUpTime: '09:00',
  dropOffDate: null,
  dropOffTime: '09:00',
  carType: '',
  vendor: ''
};
const useCarSearch = config => {
  const mergedInitialState = {
    ...defaultState$1,
    ...(config.initialValues || {})
  };
  const formik = useFormik({
    initialValues: mergedInitialState,
    validate: values => {
      try {
        carSchema.validateSync(values, {
          abortEarly: false,
          context: {
            ...values,
            ...(config.additionalContext || {})
          }
        });
      } catch (error) {
        if (error.name !== 'ValidationError') {
          throw error;
        }
        return error.inner.reduce((errors, currentError) => {
          const newErrors = lodash.set(errors, currentError.path, currentError.message);
          return newErrors;
        }, {});
      }
      return {};
    },
    validateOnBlur: true,
    onSubmit: async values => {
      config.onSubmit(values);
    }
  });
  const {
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    submitForm,
    validateForm
  } = formik;
  const getFieldTouched = field => {
    var _formik$touched;
    return (_formik$touched = formik.touched) === null || _formik$touched === void 0 ? void 0 : _formik$touched[field];
  };
  const getErrorMessage = field => {
    var _formik$errors;
    return getFieldTouched(field) && ((_formik$errors = formik.errors) === null || _formik$errors === void 0 ? void 0 : _formik$errors[field]);
  };
  const actions = {
    handleBlur,
    submitForm,
    handleChange,
    validateForm,
    setFieldTouched,
    setFieldValue,
    getFieldTouched,
    getErrorMessage
  };
  const {
    values,
    errors,
    touched
  } = formik;
  return [{
    values,
    errors,
    touched
  }, actions];
};

const CAR_SEARCH_TYPES = {
  DEFAULT: 'default'
};
const CAR_TIME_OPTIONS = [{
  value: '00:00',
  label: '12:00 am'
}, {
  value: '00:30',
  label: '12:30 am'
}, {
  value: '01:00',
  label: '1:00 am'
}, {
  value: '01:30',
  label: '1:30 am'
}, {
  value: '02:00',
  label: '2:00 am'
}, {
  value: '02:30',
  label: '2:30 am'
}, {
  value: '03:00',
  label: '3:00 am'
}, {
  value: '03:30',
  label: '3:30 am'
}, {
  value: '04:00',
  label: '4:00 am'
}, {
  value: '04:30',
  label: '4:30 am'
}, {
  value: '05:00',
  label: '5:00 am'
}, {
  value: '05:30',
  label: '5:30 am'
}, {
  value: '06:00',
  label: '6:00 am'
}, {
  value: '06:30',
  label: '6:30 am'
}, {
  value: '07:00',
  label: '7:00 am'
}, {
  value: '07:30',
  label: '7:30 am'
}, {
  value: '08:00',
  label: '8:00 am'
}, {
  value: '08:30',
  label: '8:30 am'
}, {
  value: '09:00',
  label: '9:00 am'
}, {
  value: '09:30',
  label: '9:30 am'
}, {
  value: '10:00',
  label: '10:00 am'
}, {
  value: '10:30',
  label: '10:30 am'
}, {
  value: '11:00',
  label: '11:00 am'
}, {
  value: '11:30',
  label: '11:30 am'
}, {
  value: '12:00',
  label: '12:00 pm'
}, {
  value: '12:30',
  label: '12:30 pm'
}, {
  value: '13:00',
  label: '1:00 pm'
}, {
  value: '13:30',
  label: '1:30 pm'
}, {
  value: '14:00',
  label: '2:00 pm'
}, {
  value: '14:30',
  label: '2:30 pm'
}, {
  value: '15:00',
  label: '3:00 pm'
}, {
  value: '15:30',
  label: '3:30 pm'
}, {
  value: '16:00',
  label: '4:00 pm'
}, {
  value: '16:30',
  label: '4:30 pm'
}, {
  value: '17:00',
  label: '5:00 pm'
}, {
  value: '17:30',
  label: '5:30 pm'
}, {
  value: '18:00',
  label: '6:00 pm'
}, {
  value: '18:30',
  label: '6:30 pm'
}, {
  value: '19:00',
  label: '7:00 pm'
}, {
  value: '19:30',
  label: '7:30 pm'
}, {
  value: '20:00',
  label: '8:00 pm'
}, {
  value: '20:30',
  label: '8:30 pm'
}, {
  value: '21:00',
  label: '9:00 pm'
}, {
  value: '21:30',
  label: '9:30 pm'
}, {
  value: '22:00',
  label: '10:00 pm'
}, {
  value: '22:30',
  label: '10:30 pm'
}, {
  value: '23:00',
  label: '11:00 pm'
}, {
  value: '23:30',
  label: '11:30 pm'
}];
const CAR_TYPE_OPTIONS = [{
  value: '',
  label: 'All'
}, {
  value: 'CPT',
  label: 'Compact or Larger'
}, {
  value: 'MID',
  label: 'Mid Size or Larger'
}, {
  value: 'STD',
  label: 'Standard or Larger'
}, {
  value: 'FUL',
  label: 'Full Size or Larger'
}, {
  value: 'SUV',
  label: 'SUV'
}, {
  value: 'VAN',
  label: 'Van'
}];
const CAR_VENDOR_OPTIONS = [{
  value: '',
  label: '(All)'
}, {
  value: 'PREF',
  label: 'Preferred Vendors Only'
}, {
  value: 'AC',
  label: 'Ace Rent-a-Car '
}, {
  value: 'AD',
  label: 'Advantage Rent-a-Car'
}, {
  value: 'AL',
  label: 'Alamo Rent-a-Car'
}, {
  value: 'ZI',
  label: 'Avis Rent-a-Car'
}, {
  value: 'ZD',
  label: 'Budget Rent-a-Car'
}, {
  value: 'ZR',
  label: 'Dollar Rent-a-Car'
}, {
  value: 'ET',
  label: 'Enterprise Rent-a-Car'
}, {
  value: 'FX',
  label: 'Fox Rent-a-Car'
}, {
  value: 'GO',
  label: 'Go Rentals'
}, {
  value: 'ZE',
  label: 'Hertz Rent-a-Car'
}, {
  value: 'ZL',
  label: 'National Car Rental'
}, {
  value: 'ZA',
  label: 'Payless Car Rental'
}, {
  value: 'SX',
  label: 'Sixt Rent-a-Car'
}, {
  value: 'ZT',
  label: 'Thrifty Car Rental'
}];

const standardPayloadAdapter$1 = formValues => {
  let dropOffLocID = formValues.pickup;
  if (formValues.dropoff && formValues.dropoff) {
    dropOffLocID = formValues.dropoff;
  }
  const payload = {
    pickUpLocID: formValues.pickup,
    dropOffLocID,
    pickUp: `${isoExtendedFormat(formValues.pickupDate)}  ${formValues.pickupTime}`,
    dropOff: `${isoExtendedFormat(formValues.dropoffDate)}  ${formValues.dropoffTime}`
  };
  return payload;
};

function dateRangeValidator() {
  const {
    checkIn,
    checkOut
  } = this.parent;
  if (!isValid(checkOut)) {
    return true;
  }
  return isBefore(checkIn, checkOut);
}
function dateNotBeforeToday() {
  const {
    checkIn
  } = this.parent;
  return !isBefore(checkIn, startOfToday());
}
function canBookAdditionalRooms(value) {
  const {
    isPersonal,
    hotelAllowMultiRoom,
    invitationToken
  } = this.options.context;
  const noToken = invitationToken === undefined || invitationToken === null;
  if (!isPersonal && !hotelAllowMultiRoom && noToken) {
    return value === 1;
  }
  return true;
}
var hotelSchema = yup.object().shape({
  destination: yup.string().typeError('A destination is required.'),
  checkIn: yup.date().typeError('A check in date is required.').test('is-not-before-today', 'Date cannot be in the past.', dateNotBeforeToday),
  checkOut: yup.date().typeError('A check out date is required.').test('valid-date-range', 'Check out must be after check in.', dateRangeValidator),
  numRooms: yup.number().integer().moreThan(0).test('additional-rooms-allowed', 'Only one room can be booked for business travel.', canBookAdditionalRooms)
});

const child = {
  key: 'c1',
  age: 0
};

// Modifier is used prevent key collision when newChild is invoked too fast
const newChild = function (rmIdx) {
  let modifier = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return {
    ...child,
    key: `${rmIdx}_c_${Date.now() + modifier}`
  };
};
const room = {
  key: 'r1',
  adults: 1,
  numChildren: 0,
  children: [newChild('r1')]
};

// Modifier is used prevent key collision when newRoom is invoked too fast
const newRoom = function () {
  let modifier = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  const timestamp = Date.now() + modifier;
  const rmId = `r_${timestamp}`;
  return {
    ...room,
    key: rmId,
    children: [newChild(rmId)]
  };
};
const defaultState = {
  destination: null,
  checkIn: null,
  checkOut: null,
  numRooms: 1,
  chainCode: '',
  hotelClass: '31',
  nameSearch: '',
  rooms: [newRoom()]
};
const useHotelSearch = config => {
  const mergedInitialState = {
    ...defaultState,
    ...config.initialValues
  };
  const formik = useFormik({
    initialValues: mergedInitialState,
    validate: values => {
      try {
        hotelSchema.validateSync(values, {
          abortEarly: false,
          context: {
            ...values,
            ...(config.additionalContext || {})
          }
        });
      } catch (error) {
        if (error.name !== 'ValidationError') {
          throw error;
        }
        return error.inner.reduce((errors, currentError) => {
          const newErrors = lodash.set(errors, currentError.path, currentError.message);
          return newErrors;
        }, {});
      }
      return {};
    },
    validateOnBlur: true,
    onSubmit: async values => {
      config.onSubmit(values);
    }
  });
  const {
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    submitForm,
    validateForm
  } = formik;
  const setNumRooms = _ref => {
    let {
      target
    } = _ref;
    const numRooms = +target.value;
    setFieldValue('numRooms', +numRooms);
    setFieldTouched('numRooms');
    const {
      rooms
    } = formik.values;
    const roomDiff = numRooms - rooms.length;
    if (roomDiff) {
      const newRoomArr = [...rooms];
      for (let x = 0; x < roomDiff; x++) {
        newRoomArr.push(newRoom(x + 1));
      }
      setFieldValue('rooms', newRoomArr);
    }
  };
  const setOccupancyFieldValue = (roomIdx, field, value) => setFieldValue(`rooms.${roomIdx}.${field}`, value);
  const setNumChildren = (roomIdx, field, value) => {
    setOccupancyFieldValue(roomIdx, field, value);
    const {
      rooms
    } = formik.values;
    const {
      children
    } = rooms[roomIdx];
    const newChildrenNeeded = value - rooms[roomIdx].children.length;
    if (newChildrenNeeded) {
      const newChildArr = [...children];
      for (let x = 0; x < newChildrenNeeded; x++) {
        newChildArr.push(newChild(roomIdx, x + 1));
      }
      setFieldValue(`rooms.${roomIdx}.children`, newChildArr);
    }
  };
  const setChildFieldValue = (roomIdx, childIdx, value) => {
    setFieldValue(`rooms.${roomIdx}.children.${childIdx}.age`, value);
  };
  const getFieldTouched = field => {
    var _formik$touched;
    return (_formik$touched = formik.touched) === null || _formik$touched === void 0 ? void 0 : _formik$touched[field];
  };
  const getErrorMessage = field => {
    var _formik$errors;
    return getFieldTouched(field) && ((_formik$errors = formik.errors) === null || _formik$errors === void 0 ? void 0 : _formik$errors[field]);
  };
  const actions = {
    handleBlur,
    submitForm,
    handleChange,
    validateForm,
    setFieldTouched,
    setFieldValue,
    setNumRooms,
    setOccupancyFieldValue,
    setChildFieldValue,
    setNumChildren,
    getFieldTouched,
    getErrorMessage
  };
  const {
    values,
    errors,
    touched
  } = formik;
  return [{
    values,
    errors,
    touched
  }, actions];
};

const createOneRoomString = room => {
  // This makes a string of the form '<number of adults>,<age of first child>,<age of second child>,...'
  return `${room.adults}${room.numChildren > 0 ? ',' : ''}${room.children.slice(0, room.numChildren) // only include child fields up to the selected count
  .map(child => child.age || 0) // we want the ages
  .join(',')}`; // seperated by a comma
};
const createRoomString = values => {
  // Makes a string that encodes the room occupants as:
  // <first room string>|<second room string>|...
  return values.rooms.slice(0, values.numRooms).map(createOneRoomString).join('|');
};
const standardPayloadAdapter = (formValues, urlHSS) => {
  // we accept the url HSS parameter and only sometimes use it.
  let HSS = 'H';
  if (typeof urlHSS !== 'undefined' && (urlHSS === 'T' || urlHSS === 'C')) {
    HSS = urlHSS;
  }
  const payload = {
    checkin: isoExtendedFormat(formValues.checkIn),
    checkout: isoExtendedFormat(formValues.checkOut),
    locId: formValues.destination,
    rms: createRoomString(formValues),
    HSS
  };
  return payload;
};

const HOTEL_SEARCH_TYPES = {
  DEFAULT: 'default'
};
const HOTEL_CHAIN_OPTIONS = [{
  value: '',
  label: '(All)'
}, {
  value: 'PREF',
  label: 'Preferred Properties Only'
}, {
  value: 'RT',
  label: 'AccorHotels'
}, {
  value: 'EC',
  label: 'Choice Family'
}, {
  value: 'FW',
  label: 'Fairmont Hotels'
}, {
  value: 'EH',
  label: 'Hilton Family'
}, {
  value: 'HY',
  label: 'Hyatt'
}, {
  value: '6C',
  label: 'Intercontinental Family'
}, {
  value: 'EM',
  label: 'Marriott Family'
}, {
  value: 'PV',
  label: 'Preferred Hotels & Resorts'
}, {
  value: 'CW',
  label: 'Radisson Hotel Group'
}, {
  value: 'WR',
  label: 'Wyndham Hotels & Resorts'
}, {
  value: 'AL',
  label: 'Aloft'
}, {
  value: 'AE',
  label: 'Amerihost'
}, {
  value: 'BW',
  label: 'Best Western'
}, {
  value: 'EZ',
  label: 'Cambia Suites'
}, {
  value: 'YO',
  label: 'Candlewood Suites'
}, {
  value: 'CC',
  label: 'Clarion Collection'
}, {
  value: 'CQ',
  label: 'Club Quarters'
}, {
  value: 'CI',
  label: 'Comfort Inns'
}, {
  value: 'CZ',
  label: 'Comfort Suites'
}, {
  value: 'CX',
  label: 'Country Hospitality'
}, {
  value: 'CY',
  label: 'Courtyard'
}, {
  value: 'CP',
  label: 'Crowne Plaza'
}, {
  value: 'DI',
  label: 'Days Inn'
}, {
  value: 'DT',
  label: 'Doubletree'
}, {
  value: 'DR',
  label: 'Drury Hotels'
}, {
  value: 'EO',
  label: 'Econo Lodge'
}, {
  value: 'ES',
  label: 'Embassy Suites'
}, {
  value: 'FN',
  label: 'Fairfield Inn'
}, {
  value: 'FA',
  label: 'Fairmont Hotels'
}, {
  value: 'FP',
  label: 'Four Points'
}, {
  value: 'FS',
  label: 'Four Seasons'
}, {
  value: 'HX',
  label: 'Hampton Inns'
}, {
  value: 'BH',
  label: 'Hawthorn Suites'
}, {
  value: 'HH',
  label: 'Hilton'
}, {
  value: 'GI',
  label: 'Hilton Garden Inns'
}, {
  value: 'HI',
  label: 'Holiday Inn'
}, {
  value: 'HT',
  label: 'Home2Suites'
}, {
  value: 'HJ',
  label: 'Howard Johnson'
}, {
  value: 'IC',
  label: 'Intercontinental'
}, {
  value: 'KC',
  label: 'Kimpton'
}, {
  value: 'LQ',
  label: 'La Quinta Inn'
}, {
  value: 'MD',
  label: 'Le Meridien'
}, {
  value: 'LZ',
  label: 'Loews Hotels'
}, {
  value: 'MZ',
  label: 'Mainstay Suites'
}, {
  value: 'MC',
  label: 'Marriott'
}, {
  value: 'MV',
  label: 'MGM Resorts'
}, {
  value: 'MT',
  label: 'Microtel'
}, {
  value: 'MN',
  label: 'Montage'
}, {
  value: 'OM',
  label: 'Omni Hotels'
}, {
  value: 'QI',
  label: 'Quality Inn'
}, {
  value: 'RD',
  label: 'Radisson'
}, {
  value: 'RA',
  label: 'Ramada'
}, {
  value: 'RF',
  label: 'Red Roof Inn'
}, {
  value: 'RE',
  label: 'Regent International'
}, {
  value: 'BR',
  label: 'Renaissance'
}, {
  value: 'RC',
  label: 'Residence Inn'
}, {
  value: 'RZ',
  label: 'Ritz Carlton'
}, {
  value: 'RI',
  label: 'Rodeway Inn'
}, {
  value: 'SI',
  label: 'Sheraton'
}, {
  value: 'SZ',
  label: 'Sleep Inns'
}, {
  value: 'SN',
  label: 'Sonesta Collection'
}, {
  value: 'XV',
  label: 'Springhill Suites'
}, {
  value: 'XR',
  label: 'St. Regis'
}, {
  value: 'YZ',
  label: 'Staybridge Suites'
}, {
  value: 'XL',
  label: 'Summit Hotels'
}, {
  value: 'OZ',
  label: 'Super 8'
}, {
  value: 'SL',
  label: 'Swissotel'
}, {
  value: 'TO',
  label: 'Townplace Suites'
}, {
  value: 'TL',
  label: 'Travelodge'
}, {
  value: 'RU',
  label: 'Tru by Hilton'
}, {
  value: 'VL',
  label: 'Virgin Hotels'
}, {
  value: 'WH',
  label: 'W Hotels'
}, {
  value: 'WK',
  label: 'Warwick Hotels'
}, {
  value: 'WI',
  label: 'Westin'
}, {
  value: 'WW',
  label: 'World Hotels'
}, {
  value: 'WY',
  label: 'Wyndham'
}];
const HOTEL_NUMBER_OF_ROOM_OPTIONS = [{
  value: '1',
  label: '1 Room'
}, {
  value: '2',
  label: '2 Rooms'
}, {
  value: '3',
  label: '3 Rooms'
}, {
  value: '4',
  label: '4 Rooms'
}];
const HOTEL_CLASS_OPTIONS = [{
  value: '31',
  label: '(All)'
}, {
  value: '30',
  label: '2 stars and above'
}, {
  value: '28',
  label: '3 stars and above'
}, {
  value: '24',
  label: '4 stars and above'
}, {
  value: '16',
  label: '5 stars only'
}];
const HOTEL_ADULT_COUNT_OPTIONS = [{
  value: '1',
  label: '1 Adult'
}, {
  value: '2',
  label: '2 Adults'
}, {
  value: '3',
  label: '3 Adults'
}, {
  value: '4',
  label: '4 Adults'
}, {
  value: '5',
  label: '5 Adults'
}, {
  value: '6',
  label: '6 Adults'
}];
const HOTEL_CHILD_COUNT_OPTIONS = [{
  value: '0',
  label: 'No Children'
}, {
  value: '1',
  label: '1 Child'
}, {
  value: '2',
  label: '2 Children'
}, {
  value: '3',
  label: '3 Children'
}];
const HOTEL_CHILD_AGE_OPTIONS = [{
  value: '0',
  label: '< 1 yr'
}, {
  value: '1',
  label: '1 yr'
}, {
  value: '2',
  label: '2 yrs'
}, {
  value: '3',
  label: '3 yrs'
}, {
  value: '4',
  label: '4 yrs'
}, {
  value: '5',
  label: '5 yrs'
}, {
  value: '6',
  label: '6 yrs'
}, {
  value: '7',
  label: '7 yrs'
}, {
  value: '8',
  label: '8 yrs'
}, {
  value: '9',
  label: '9 yrs'
}, {
  value: '10',
  label: '10 yrs'
}, {
  value: '11',
  label: '11 yrs'
}, {
  value: '12',
  label: '12 yrs'
}, {
  value: '13',
  label: '13 yrs'
}, {
  value: '14',
  label: '14 yrs'
}, {
  value: '15',
  label: '15 yrs'
}, {
  value: '16',
  label: '16 yrs'
}, {
  value: '17',
  label: '17 yrs'
}];

const CardTextContainer = styled(Flex)`
  gap: 4px;
  flex-direction: column;
`;
const TicketsContainer = styled(Flex)`
  gap: 8px;
  flex-direction: column;
`;
const ExpiredText = styled.div`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const Header$1 = styled.div`
  padding: 8px 0px;
  margin-bottom: 2px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;
const SupportLinkContainer = styled.div`
  padding: 8px 0px;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    color: ${getTokenValue(THEME_TOKENS.base.color.link)};
  }
`;
const SupportText = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: -2px;
  margin-right: 4px;
`;
const Content$1 = styled.div`
  max-height: ${_ref => {
  let {
    isVisible
  } = _ref;
  return isVisible ? '100vh' : 0;
}};
  transition: max-height ${_ref2 => {
  let {
    isVisible
  } = _ref2;
  return isVisible ? '1s' : '0.5s';
}} ease-in-out;
  overflow: hidden;
  ${media.Medium`
    max-height: unset;
    overflow: unset;
  `}
`;
const IconCardContainer = styled.a`
  text-decoration: none;
  color: ${getThemeProp('global.secondary--color')};
`;
const HeaderTouchable$1 = styled(Flex)`
  background: ${getTokenValue(THEME_TOKENS.base.color.white)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
  font-size: 16px;
  font-weight: 600;
  padding: 16px;
  margin-bottom: 4px;
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  border-color: ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
`;
const IconAddRemove$1 = styled.div`
  height: 20px;
  font-size: 20px;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
`;
const navigateToSupport = supportLink => {
  window.open(supportLink, '_blank');
};
const makePartiallyUsedText = () => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, "TBD", /*#__PURE__*/React.createElement(InfoTooltip, {
    content: "This ticket has been partially flown. The remaining credit will vary depending on the new itinerary. Please contact our support team to use this credit."
  }));
};
const UnusedTickets = _ref3 => {
  let {
    tickets = [],
    travelerName,
    collapsible = true,
    supportLink = null,
    withLink = false,
    showPartiallyUsed = true
  } = _ref3;
  const [isVisible, setIsVisible] = useState(false);
  const filteredTickets = tickets.filter(ticket => {
    if (!showPartiallyUsed) {
      return !ticket.partiallyUsed;
    }
    return true;
  });
  // only tickets array provided
  if (filteredTickets.length !== 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, travelerName ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Header$1, null, travelerName)), collapsible ? /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HeaderTouchable$1, {
      onClick: () => {
        setIsVisible(!isVisible);
      }
    }, "Unused Tickets for ", travelerName, /*#__PURE__*/React.createElement(IconAddRemove$1, null, isVisible ? /*#__PURE__*/React.createElement(IconLine, {
      icon: "Minus"
    }) : /*#__PURE__*/React.createElement(IconLine, {
      icon: "Plus"
    })))) : /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Header$1, null, travelerName))) : null, /*#__PURE__*/React.createElement(Content$1, {
      isVisible: isVisible || !collapsible
    }, /*#__PURE__*/React.createElement(TicketsContainer, null, filteredTickets.map(ticket => {
      const formattedDate = shortMonthDayYear(parseDate(ticket.expiresDate));
      const jsr = encodeObjToJsrString({
        filterState: {
          2: ticket.name
        },
        historyReportId: 7,
        dt: 1
      });
      return /*#__PURE__*/React.createElement(CardWithIcon, {
        icon: /*#__PURE__*/React.createElement(AirlineLogo, {
          alCode: ticket.airlineCode,
          size: "22px"
        }),
        key: ticket.ticketNumber
      }, /*#__PURE__*/React.createElement(IconCardContainer, {
        href: `/reports/?jsr=${jsr}`,
        target: "_blank",
        as: withLink ? 'a' : 'div'
      }, /*#__PURE__*/React.createElement(CardTextContainer, null, /*#__PURE__*/React.createElement(Text, {
        weight: "bold"
      }, ticket.airline), /*#__PURE__*/React.createElement(Text, null, "Credit Value:", ' ', ticket.partiallyUsed ? makePartiallyUsedText() : /*#__PURE__*/React.createElement(Currency, {
        amount: ticket.creditValue
      })), /*#__PURE__*/React.createElement(ExpiredText, null, /*#__PURE__*/React.createElement(Text, null, "Expires ", formattedDate)), ticket.isPersonal !== 'Business' ? /*#__PURE__*/React.createElement(Box, {
        pt: 2
      }, /*#__PURE__*/React.createElement(Badge, null, "Personal")) : null)));
    })), supportLink ? /*#__PURE__*/React.createElement(SupportLinkContainer, {
      onClick: () => {
        navigateToSupport(supportLink);
      }
    }, /*#__PURE__*/React.createElement(SupportText, null, "How to use unused tickets"), /*#__PURE__*/React.createElement(IconOpenNew, null)) : null));
  }

  // no tickets array provided
  return /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(EmptyState, {
    subtle: true,
    size: "fullWidth",
    text: "No unused tickets available",
    icon: /*#__PURE__*/React.createElement(IconLine, {
      icon: "PlaneTicket",
      size: "large"
    })
  }));
};
UnusedTickets.propTypes = {
  /**
   * Name of the Traveler that the tickets belong to
   */
  travelerName: PropTypes.string,
  /**
   * Array of tickets from the TravelerConfig
   */
  tickets: PropTypes.arrayOf(PropTypes.shape({
    airlineCode: PropTypes.string,
    airline: PropTypes.string,
    creditValue: PropTypes.number,
    expiresDate: PropTypes.string
  })),
  /**
   * Controls whether on small screens, component collapses into accordion
   */
  collapsible: PropTypes.bool,
  /**
   * Link for support article on unused tickets
   */
  supportLink: PropTypes.string,
  /**
   * Cards will be clickable and opens a  new tab to reports id=7 page that will be filtered by the clicked cards name
   */
  withLink: PropTypes.bool,
  /**
   * Shows tickets that are partially used
   */
  showPartiallyUsed: PropTypes.bool
};

const SearchTitleText = styled.div`
  margin-bottom: 2px;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  ${media.Medium`
    font-size: 16px;
    margin-bottom: 2px;
    line-height: 20px;
  `};
`;
const SearchDatesText = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const SearchSubtitleText = styled.div`
  padding-top: 4px;
  font-size: 13px;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
  padding-bottom: 8px;
  line-height: 1.2;
`;
const SearchDatesSeperatorText = styled.span`
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
  margin: 0 4px 0 4px;
`;

const RoundTripSearch = _ref => {
  let {
    legs
  } = _ref;
  const {
    fromLocationName,
    toLocationName,
    startDate
  } = legs[0];
  const {
    endDate
  } = legs[1];
  const formattedStartDate = shortDayMonthDate(startDate).toUpperCase();
  const formattedEndDate = shortDayMonthDate(endDate).toUpperCase();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchTitleText, null, fromLocationName, " to ", toLocationName), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedStartDate), /*#__PURE__*/React.createElement(SearchDatesSeperatorText, null, " to "), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedEndDate));
};
RoundTripSearch.propTypes = {
  /**
   * array of legs of the trip
   */
  legs: PropTypes.arrayOf(PropTypes.shape({
    fromLocationName: PropTypes.string,
    toLocationName: PropTypes.string,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date)
  }))
};

const LegContainer = styled.div``;
const LegsContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
`;
const MultiCitySearch = _ref => {
  let {
    legs
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchTitleText, null, "Multi-City"), /*#__PURE__*/React.createElement(LegsContainer, null, legs.map(leg => {
    const {
      fromLocationName,
      toLocationName,
      startDate
    } = leg;
    const formattedStartDate = shortDayMonthDate(startDate);
    const uniqueKey = fromLocationName + toLocationName + startDate;
    return /*#__PURE__*/React.createElement(LegContainer, {
      key: uniqueKey
    }, /*#__PURE__*/React.createElement(SearchSubtitleText, null, fromLocationName, " to ", toLocationName), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedStartDate));
  })));
};
MultiCitySearch.propTypes = {
  /**
   * Array of legs of the trip
   */
  legs: PropTypes.arrayOf(PropTypes.shape({
    fromLocationName: PropTypes.string,
    toLocationName: PropTypes.string,
    startDate: PropTypes.instanceOf(Date)
  }))
};

const CarSearch = props => {
  const {
    pickUpLoc,
    dropOffLoc,
    pickUpDate,
    dropOffDate
  } = props;
  const formattedStartDate = shortDayMonthDate(pickUpDate);
  const formattedEndDate = shortDayMonthDate(dropOffDate);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchTitleText, null, pickUpLoc), pickUpLoc === dropOffLoc ? null : /*#__PURE__*/React.createElement(SearchSubtitleText, null, /*#__PURE__*/React.createElement("b", null, "Drop Off:"), " ", dropOffLoc), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedStartDate), /*#__PURE__*/React.createElement(SearchDatesSeperatorText, null, " to "), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedEndDate));
};
CarSearch.propTypes = {
  pickUpLoc: PropTypes.string,
  dropOffLoc: PropTypes.string,
  pickUpDate: PropTypes.instanceOf(Date),
  dropOffDate: PropTypes.instanceOf(Date)
};

const HotelSearch = props => {
  const {
    destination,
    checkInDate,
    checkOutDate
  } = props;
  const formattedStartDate = shortDayMonthDate(checkInDate);
  const formattedEndDate = shortDayMonthDate(checkOutDate);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchTitleText, null, destination), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedStartDate), /*#__PURE__*/React.createElement(SearchDatesSeperatorText, null, " to "), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedEndDate));
};
HotelSearch.propTypes = {
  /**
   * Destination as a string
   */
  destination: PropTypes.string,
  /**
   * Date object of check in
   */
  checkInDate: PropTypes.instanceOf(Date),
  /**
   * Date object of check out
   */
  checkOutDate: PropTypes.instanceOf(Date)
};

const OneWaySearch = _ref => {
  let {
    legs
  } = _ref;
  const {
    fromLocationName,
    toLocationName,
    startDate
  } = legs[0];
  const formattedStartDate = shortDayMonthDate(startDate);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SearchTitleText, null, fromLocationName, " to ", toLocationName), /*#__PURE__*/React.createElement(SearchDatesText, null, formattedStartDate));
};
OneWaySearch.propTypes = {
  /**
   * Array of legs of the trip
   */
  legs: PropTypes.arrayOf(PropTypes.shape({
    fromLocationName: PropTypes.string,
    toLocationName: PropTypes.string,
    startDate: PropTypes.instanceOf(Date)
  }))
};

const Header = styled.div`
  background-color: ${getTokenValue(THEME_TOKENS.base.color.white)};
  padding: 8px;
  margin-bottom: 2px;
`;
const HeaderText = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: -2px;
`;
const Content = styled.div`
  max-height: ${_ref => {
  let {
    isVisible
  } = _ref;
  return isVisible ? '100vh' : 0;
}};
  transition: max-height ${_ref2 => {
  let {
    isVisible
  } = _ref2;
  return isVisible ? '1s' : '0.5s';
}} ease-in-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${SPACE.size8};

  ${media.Medium`
    max-height: unset;
    overflow: unset;
  `}
`;
const HeaderTouchable = styled(Flex)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
  font-size: 16px;
  font-weight: 600;
  background: ${getTokenValue(THEME_TOKENS.base.color.white)};
  padding: 16px;
  margin-bottom: 4px;
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  border-color: ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
`;
const IconAddRemove = styled.div`
  height: 20px;
  font-size: 20px;
  color: ${getTokenValue(THEME_TOKENS.base.color.link)};
`;
const getSearchTypeIcon = searchType => {
  switch (searchType) {
    case 'hotel':
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Hotel"
      });
    case 'flight':
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Plane"
      });
    case 'car':
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Car"
      });
    case 'rail':
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Train"
      });
    default:
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Plus"
      });
  }
};

// provides the body of each search card
const getSearchBody = search => {
  return /*#__PURE__*/React.createElement(Box, {
    width: "90%"
  }, search.searchType === 'car' ? /*#__PURE__*/React.createElement(CarSearch, {
    travelerName: search.travelerName,
    pickUpLoc: search.pickUpLoc,
    dropOffLoc: search.dropOffLoc,
    pickUpDate: search.pickUpDate,
    dropOffDate: search.dropOffDate
  }) : null, search.searchType === 'hotel' ? /*#__PURE__*/React.createElement(HotelSearch, {
    travelerName: search.travelerName,
    destination: search.destination,
    checkInDate: search.checkInDate,
    checkOutDate: search.checkOutDate
  }) : null, search.searchType === 'flight' && search.tripType === 'roundTrip' ? /*#__PURE__*/React.createElement(RoundTripSearch, {
    legs: search.legs,
    travelerName: search.travelerName
  }) : null, search.searchType === 'flight' && search.tripType === 'multiCity' ? /*#__PURE__*/React.createElement(MultiCitySearch, {
    legs: search.legs,
    travelerName: search.travelerName
  }) : null, search.searchType === 'flight' && search.tripType === 'oneWay' ? /*#__PURE__*/React.createElement(OneWaySearch, {
    legs: search.legs,
    travelerName: search.travelerName
  }) : null);
};
const HandleClick = dest => {
  document.location = dest;
};
const RecentSearches = _ref3 => {
  let {
    searches = [],
    travelerName,
    collapsible = true,
    showNameBadges = true
  } = _ref3;
  const [isVisible, setIsVisible] = useState(false);

  // only searches array provided
  if (searches.length !== 0) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, travelerName ? /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(HeaderText, null, travelerName))) : null, /*#__PURE__*/React.createElement(React.Fragment, null, collapsible ? /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HeaderTouchable, {
      onClick: () => {
        setIsVisible(!isVisible);
      }
    }, "Recent Searches", /*#__PURE__*/React.createElement(IconAddRemove, null, isVisible ? /*#__PURE__*/React.createElement(IconLine, {
      icon: "Minus"
    }) : /*#__PURE__*/React.createElement(IconLine, {
      icon: "Plus"
    })))) : travelerName && /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(HeaderText, null, travelerName)))), /*#__PURE__*/React.createElement(Content, {
      isVisible: isVisible || !collapsible
    }, searches.map(search => {
      return /*#__PURE__*/React.createElement(CardWithIcon, {
        key: search.link,
        onClick: () => {
          HandleClick(search.link);
        },
        icon: /*#__PURE__*/React.createElement(IconFlex, {
          size: 24,
          defaultColor: true
        }, getSearchTypeIcon(search.searchType))
      }, search.bn ? /*#__PURE__*/React.createElement(Badge, {
        size: "small",
        noBorderRadius: true,
        kind: "success"
      }, "Booking #: ", search.bn) : null, showNameBadges && search.travelerName ? /*#__PURE__*/React.createElement(Box, {
        mb: "8px"
      }, /*#__PURE__*/React.createElement(Badge, {
        size: "small",
        noBorderRadius: true,
        kind: "primary"
      }, search.travelerName)) : null, getSearchBody(search));
    })));
  }

  // no searches array provided
  return /*#__PURE__*/React.createElement(EmptyState, {
    subtle: true,
    size: "fullWidth",
    text: "No recent searches available",
    icon: /*#__PURE__*/React.createElement(IconLine, {
      icon: "Search",
      size: "large"
    })
  });
};
RecentSearches.propTypes = {
  /**
   * Name of the Traveler that the searches belong to
   */
  travelerName: PropTypes.string,
  /**
   * Array of searches from the TravelerConfig
   */
  searches: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Controls whether or not traveler name will be shown on each recent search
   */
  showNameBadges: PropTypes.bool,
  /**
   * Controls whether on small screens, component collapses into accordion
   */
  collapsible: PropTypes.bool
};

const getShortDateSummary = date => {
  if (date) {
    return `${monthShortFormatter(date)} ${dayOfMonthFormatter(date)}`;
  }
  return 'Select a Date';
};
const getTimeDisplayText = date => {
  return `${hourMinuteFormat(date)} ${meridianFormat(date)}`;
};

export { AIRLINE_OPTIONS, CABIN_OPTIONS, CAR_SEARCH_TYPES, CAR_TIME_OPTIONS, CAR_TYPE_OPTIONS, CAR_VENDOR_OPTIONS, FLIGHT_SEARCH_TYPES, FLIGHT_TIME_OPTIONS, HOTEL_ADULT_COUNT_OPTIONS, HOTEL_CHAIN_OPTIONS, HOTEL_CHILD_AGE_OPTIONS, HOTEL_CHILD_COUNT_OPTIONS, HOTEL_CLASS_OPTIONS, HOTEL_NUMBER_OF_ROOM_OPTIONS, HOTEL_SEARCH_TYPES, REFUNDABLE_OPTIONS, RecentSearches, STOPS_OK, UnusedTickets, ZERO_TO_NINE, standardPayloadAdapter$1 as carPayloadAdapter, standardPayloadAdapter$2 as flightPayloadAdapter, getShortDateSummary, getTimeDisplayText, standardPayloadAdapter as hotelPayloadAdapter, useCarSearch, useFlightSearch, useFlightSearch as useFlightSearch2, useHotelSearch };
//# sourceMappingURL=index.esm.js.map
